import axios from "axios";
import Vue from "vue";
import Vuex from "vuex";

import debounce from "lodash/debounce";
import flattenDeep from "lodash/flattenDeep";
import uniqBy from "lodash/uniqBy";

import { differenceInSeconds, parse, add } from "date-fns";

import { bus } from "../index";

Vue.use(Vuex);

const store = new Vuex.Store({
	state: {
		basket: {},
		miniBasketVisible: false,
		checkoutVisible: false,
		conditionsAccepted: false,
		checkoutState: "",
		showBasketMessage: false,
		isCheckingOut: false,
		hasTriedToCheckout: false,
		receiptContent: null,
		removedReservations: {
			visible: false,
			deletedProducts: [],
			nextCheck: null,
		},
		basketClearedByErrorVisible: false,
		validations: [],
	},
	getters: {
		missingFieldCount(state) {
			var count = 0;
			state.validations.map((v) => {
				if (v.missingFields) {
					count = count + v.missingFields;
				}
				if (v.missingPatterns) {
					count = count + v.missingPatterns;
				}
			});
			return count;
		},
		validations(state) {
			return state.validations;
		},
		hasTriedToCheckout(state) {
			return state.hasTriedToCheckout;
		},
		allowGoToPayment(state) {
			return state.conditionsAccepted && state.basket.IsValidForCheckout;
		},
		removedReservations(state) {
			return state.removedReservations;
		},
		basketClearedByError(state) {
			return state.basketClearedByErrorVisible;
		},
		basket(state) {
			return state.basket;
		},
		receiptContent(state) {
			return state.receiptContent;
		},
		isCheckingOut(state) {
			return state.isCheckingOut;
		},
		conditionsAccepted(state) {
			return state.conditionsAccepted;
		},
		quantityInBasket(state) {
			return (productId) => {
				if (!state.basket || !state.basket.OrderLines) {
					return 0;
				} else {
					var orderlines = state.basket.OrderLines.filter(
						(ol) => ol.ProductId === productId
					).map((ol) => ol.Quantity);
					var quantity = orderlines.reduce((a, b) => a + b, 0);
					return quantity;
				}
				return 0;
			};
		},
		hasBasket(state) {
			return (
				state.basket &&
				state.basket.OrderLines &&
				state.basket.OrderLines.length > 0
			);
		},
		basketTotal(state) {
			if (!state.basket || !state.basket.OrderLines) {
				return 0;
			}
			return state.basket.OrderLines.map((ol) => ol.Total).reduce(
				(a, b) => a + b,
				0
			);
		},
		miniBasketVisible(state) {
			return state.miniBasketVisible;
		},
		checkoutVisible(state) {
			return state.checkoutVisible;
		},
		distinctParticipants(state) {
			if (!state.basket || !state.basket.OrderLines) {
				return [];
			}

			let participants = state.basket.OrderLines.map((ol) => [
				ol.Participants,
				ol.Teams.map((t) => t.Participants),
			]);
			participants = flattenDeep(participants);
			participants = participants.filter(
				(participant) => participant.IsValidForPayment
			);
			participants = uniqBy(participants, (v) =>
				JSON.stringify([
					v.FirstName,
					v.LastName,
					v.Email,
					v.Address,
					v.Zip,
					v.City,
					v.Phone,
					v.Mobile,
				])
			);
			return participants;
		},
		checkoutState(state) {
			return state.basket.CheckoutMode;
		},
		showBasketMessage(state) {
			return state.showBasketMessage;
		},
		uniqueParticipants(state) {
			var participants = [];

			// loop through all orderlines with participants
			state.basket.OrderLines.filter(
				(ol) => ol.Participants && ol.Participants.length > 0
			).map((ol) => {
				// find participants, not already added to participants array
				ol.Participants.filter((p) => p.FirstName + p.LastName != "").map(
					(p) => {
						if (
							participants
								.map((part) => part.FirstName + part.LastName)
								.indexOf(p.FirstName + p.LastName) == -1
						) {
							participants.push(p);
						}
					}
				);
			});

			return participants;
		},
		firstParticipant(state) {
			var participantsWithAddresses = state.basket.OrderLines.filter(
				(ol) =>
					ol.Participants &&
					ol.Participants.length > 0 &&
					ol.Participants[0].Address &&
					ol.Participants[0].Address != ""
			);
			return participantsWithAddresses.length > 0
				? participantsWithAddresses[0].Participants[0]
				: null;
		},
	},
	mutations: {
		resetTryToCheckout: (state) => {
			state.hasTriedToCheckout = false;
		},
		tryToCheckout: (state) => {
			setTimeout(() => (state.hasTriedToCheckout = true), 10);
		},
		setRemovedReservations: (state, deletedProducts) => {
			if (deletedProducts) {
				state.removedReservations.deletedProducts = deletedProducts;
				state.removedReservations.visible = deletedProducts.length > 0;
			}
		},
		hideRemovedReservations: (state) => {
			state.removedReservations.visible = false;
			state.removedReservations.deletedProducts = [];
		},
		showBasketClearedByError: (state) => {
			state.basketClearedByErrorVisible = true;
		},
		hideBasketClearedByError: (state) => {
			state.basketClearedByErrorVisible = false;
		},
		setReceiptData: (state, receiptData) => {
			state.receiptContent = receiptData;
		},
		toggleMiniBasket: (state) => {
			state.miniBasketVisible = !state.miniBasketVisible;
			document.querySelector("body").style.overflowY =
				state.miniBasketVisible || state.checkoutVisible ? "hidden" : "visible";
			if (state.miniBasketVisible) {
				history.pushState(null, null, "#kurv");
			}
		},
		checkoutState: (state, checkoutState) => {
			state.basket.CheckoutMode = checkoutState;
		},
		isCheckingOut: (state, isCheckingOut) => {
			state.isCheckingOut = isCheckingOut;
		},
		toggleCheckout: (state) => {
			state.checkoutVisible = !state.checkoutVisible;
			if (!state.checkoutVisible) {
				state.hasTriedToCheckout = false;
			}
			document.querySelector("body").style.overflowY =
				state.miniBasketVisible || state.checkoutVisible ? "hidden" : "visible";
			if (state.checkoutVisible) {
				history.pushState(null, null, "#checkout");
			}
		},
		updateBasket: (state, data) => {
			sessionStorage.setItem("fcms_currentBasket", JSON.stringify(data.basket));
			var basket = data.basket;
			var stateBasket = state.basket;

			if (data.keepParticipantData) {
				if (stateBasket.Billing && basket.Billing) {
					stateBasket.Billing.IsValidForPayment =
						basket.Billing.IsValidForPayment;
					basket.Billing = stateBasket.Billing;
				}

				if (stateBasket.OrderLines && basket.OrderLines) {
					for (let d = 0; d < stateBasket.OrderLines.length; d++) {
						stateBasket.OrderLines[d].CanInvoice =
							basket.OrderLines[d].CanInvoice;

						for (
							let i = 0;
							i < stateBasket.OrderLines[d].Participants.length;
							i++
						) {
							stateBasket.OrderLines[d].Participants[i].IsValidForPayment =
								basket.OrderLines[d].Participants[i].IsValidForPayment;
						}

						for (let i = 0; i < stateBasket.OrderLines[d].Teams.length; i++) {
							for (
								let t = 0;
								t < stateBasket.OrderLines[d].Teams[i].Participants.length;
								t++
							) {
								stateBasket.OrderLines[d].Teams[i].Participants[
									t
								].IsValidForPayment =
									basket.OrderLines[d].Teams[i].Participants[
										t
									].IsValidForPayment;
							}
						}
						basket.OrderLines[d] = stateBasket.OrderLines[d];
					}
				}
			}

			state.basket = basket;
			bus.$emit("basketUpdated", true);

			if (basket.Now && basket.Expires) {
				var serverNow = parse(basket.Now, "yyyyMMddHHmmss", new Date());
				var serverExpires = parse(basket.Expires, "yyyyMMddHHmmss", new Date());

				var localNow = new Date();
				var localExpires = add(localNow, {
					seconds: differenceInSeconds(serverExpires, serverNow),
				});

				state.basket.LocalNow = localNow;
				state.basket.LocalExpires = localExpires;
			}

			if (basket.MemberData) {
				basket.MemberData.AcceptedFromBackend = basket.MemberData.Accepted;
			}

			state.receiptContent = null;
		},
		toggleConditionsAccepted: (state) => {
			state.conditionsAccepted = !state.conditionsAccepted;
		},
		updateMobilePayData: (state, mobilePayData) => {
			state.basket.MobilePayData = mobilePayData;
		},
		updateMemberData: (state, memberData) => {
			state.basket.MemberData = memberData;
		},
		showBasketMessage: (state) => {
			let sitenav = document.querySelector("header.sitenav");
			if (sitenav) {
				sitenav.classList.add("sticky");
				clearTimeout(window.to1);
				clearTimeout(window.to2);
				clearTimeout(window.to3);
				clearTimeout(window.to4);

				window.to1 = setTimeout(() => sitenav.classList.add("sticky-in"), 10);
				window.to2 = setTimeout(
					() => sitenav.classList.remove("sticky-in"),
					2700
				);
				window.to3 = setTimeout(() => sitenav.classList.remove("sticky"), 3000);

				state.showBasketMessage = true;
				window.to4 = setTimeout(() => (state.showBasketMessage = false), 3000);
			}
		},
		copyParticipantTo: (state, payload) => {
			if (
				payload.participant &&
				state.basket.OrderLines[payload.olIndex] &&
				state.basket.OrderLines[payload.olIndex].Participants[payload.index]
			) {
				if (!payload.onlyAddress) {
					state.basket.OrderLines[payload.olIndex].Participants[
						payload.index
					].FirstName = payload.participant.FirstName;
					state.basket.OrderLines[payload.olIndex].Participants[
						payload.index
					].LastName = payload.participant.LastName;
					state.basket.OrderLines[payload.olIndex].Participants[
						payload.index
					].Email = payload.participant.Email;
					state.basket.OrderLines[payload.olIndex].Participants[
						payload.index
					].Mobile = payload.participant.Mobile;
					state.basket.OrderLines[payload.olIndex].Participants[
						payload.index
					].Gender = payload.participant.Gender;
					state.basket.OrderLines[payload.olIndex].Participants[
						payload.index
					].CompanyName = payload.participant.CompanyName;
					state.basket.OrderLines[payload.olIndex].Participants[
						payload.index
					].DateOfBirth = payload.participant.DateOfBirth;
					state.basket.OrderLines[payload.olIndex].Participants[
						payload.index
					].DateOfBirthString = payload.participant.DateOfBirthString;
					state.basket.OrderLines[payload.olIndex].Participants[
						payload.index
					].FirstName = payload.participant.FirstName;
				}
				state.basket.OrderLines[payload.olIndex].Participants[
					payload.index
				].Address = payload.participant.Address;
				state.basket.OrderLines[payload.olIndex].Participants[
					payload.index
				].Zip = payload.participant.Zip;
				state.basket.OrderLines[payload.olIndex].Participants[
					payload.index
				].City = payload.participant.City;
			}
		},
		reportValidation: (state, payload) => {
			var foundIndex = false;
			state.validations = state.validations.map((v) => {
				if (v.index == payload.index && v.olIndex == payload.olIndex) {
					v.missingFields = payload.missingFields;
					v.missingPatterns = payload.missingPatterns;
					foundIndex = true;
				}
				return v;
			});

			if (!foundIndex) {
				state.validations.push(payload);
			}
		},
	},
	actions: {
		reportValidation: (context, payload) => {
			context.commit("reportValidation", payload);
		},
		tryingToCheckout: (context) => context.commit("tryToCheckout"),
		resetTryingToCheckout: (context) => context.commit("resetTryToCheckout"),
		getExpiredReservations: debounce((context) => {
			axios
				.get("/Umbraco/Surface/BasketSurface/RemoveExpiredReservations")
				.then((response) => {
					if (response.data.deletedProducts) {
						context.commit(
							"setRemovedReservations",
							response.data.deletedProducts
						);
						if (context.state.removedReservations.nextCheck) {
							window.clearTimeout(context.state.removedReservations);
						}
						context.state.removedReservations.nextCheck = window.setTimeout(
							() => context.dispatch("getExpiredReservations"),
							response.data.nextExpiry
						);
					}
				});
		}, 100),
		hideRemovedReservations: (context) =>
			context.commit("hideRemovedReservations"),
		hideBasketClearedByError: (context) =>
			context.commit("hideBasketClearedByError"),
		setRoute: (context, payload) => {
			if (payload == "#kurv") {
				if (context.state.checkoutVisible) {
					context.commit("toggleCheckout");
				}
				if (!context.state.miniBasketVisible) {
					context.commit("toggleMiniBasket");
				}
			} else if (payload == "#checkout") {
				if (!context.state.miniBasketVisible) {
					context.commit("toggleMiniBasket");
				}
				if (!context.state.checkoutVisible) {
					context.commit("toggleCheckout");
				}
			} else {
				if (context.state.checkoutVisible) {
					context.commit("toggleCheckout");
				}
				if (context.state.miniBasketVisible) {
					context.commit("toggleMiniBasket");
				}
			}
			context.dispatch("updateBasket");
			context.dispatch("getExpiredReservations");
		},
		resetCheckout: (context) => {
			if (context.state.checkoutVisible) {
				context.commit("toggleCheckout");
			}
			if (context.state.miniBasketVisible) {
				context.commit("toggleMiniBasket");
			}
			context.commit("updateBasket", {});
			//context.dispatch("updateBasket");
		},
		toggleMiniBasket: (context) => {
			context.commit("toggleMiniBasket");
		},
		toggleCheckout: (context, payload) => {
			context.commit("checkoutState", payload);
			context.commit("toggleCheckout");
		},
		toggleConditionsAccepted: (context) => {
			context.commit("toggleConditionsAccepted");
		},
		loadBasket: (context) => {
			let basketFromSession = sessionStorage.getItem("fcms_currentBasket");
			if (basketFromSession) {
				let basket = JSON.parse(basketFromSession);
				basket.Now = null;
				basket.Expires = null;
				context.commit("updateBasket", { basket });
				context.dispatch("getExpiredReservations");
				context.dispatch("updateBasket");
			} else {
				context.dispatch("updateBasket");
			}
		},
		toggleIfEmpty: (context) => {
			if (
				!context.state.basket.OrderLines ||
				context.state.basket.OrderLines.length == 0
			) {
				if (window.location.hash == "#kurv") {
					history.replaceState(
						null,
						null,
						window.location.href.replace(window.location.hash, "")
					);
				}

				if (context.state.miniBasketVisible) {
					context.commit("toggleMiniBasket");
				}

				if (window.location.hash == "#checkout") {
					history.replaceState(
						null,
						null,
						window.location.href.replace(window.location.hash, "")
					);
				}

				if (context.state.checkoutVisible) {
					context.commit("toggleCheckout");
					context.commit("showBasketClearedByError");
				}
			}
		},
		updateBasket: debounce((context) => {
			return new Promise((resolve) => {
				axios
					.get("/umbraco/Checkout/Basket/Get")
					.then((response) => {
						context.commit("updateBasket", { basket: response.data.Data });
						context.dispatch("getExpiredReservations");

						context.dispatch("toggleIfEmpty");
						resolve();
					})
					.catch((error) => {
						console.log(error);
						resolve();
					});
			});
		}, 100),
		saveBasket: debounce((context, payload) => {
			context.dispatch("saveBasketNow", payload);
		}, 1000),
		saveBasketNow: (context, payload) => {
			if (context.state.isCheckingOut || context.state.receiptContent) {
				console.log("can't save now, is checking out");
			} else {
				return new Promise((resolve) => {
					axios
						.post("/umbraco/Checkout/Basket/SaveBasket", { basket: payload })
						.then((response) => {
							context.commit("updateBasket", {
								basket: response.data.Data,
								keepParticipantData: true,
							});
							context.dispatch("getExpiredReservations");
							context.dispatch("toggleIfEmpty");
							resolve();
						})
						.catch((error) => {
							console.log(error);
							resolve();
						});
				});
			}
		},
		addToBasket: (context, payload) => {
			return new Promise((resolve, reject) => {
				axios
					.post("/umbraco/Checkout/Basket/Add", payload)
					.then((response) => {
						if (response.data.Success) {
							context.commit("updateBasket", { basket: response.data.Data });
							context.dispatch("getExpiredReservations");
							context.commit("showBasketMessage");

							if (
								response.data.Data.OrderLines &&
								response.data.Data.OrderLines.length == 1 &&
								response.data.Data.OrderLines[0].Quantity == 1 &&
								!context.state.miniBasketVisible
							) {
								context.commit("toggleMiniBasket");
							}
						} else {
							reject({
								noSuccess: true,
								message: response.data.Message,
							});
						}
						resolve();
					})
					.catch((error) => {
						console.log(error);
						reject({
							noSuccess: false,
							error,
						});
					});
			});
		},
		addToWaitingList: (context, payload) => {
			return new Promise((resolve, reject) => {
				axios
					.post("/umbraco/Checkout/Basket/AddToWaitingList", payload)
					.then((response) => {
						if (response.data.Success) {
							resolve();
						} else {
							alert(response.data.Message);
							reject();
						}
					})
					.catch((error) => {
						console.log(error);
						reject();
					});
			});
		},
		addTimeslotToBasket: (context, payload) => {
			return new Promise((resolve, reject) => {
				axios
					.post("/umbraco/Checkout/Basket/AddTimeslot", payload)
					.then((response) => {
						if (response.data.Success) {
							context.commit("updateBasket", { basket: response.data.Data });
							context.dispatch("getExpiredReservations");
							context.commit("showBasketMessage");
						} else {
							reject({
								noSuccess: true,
								message: response.data.Message,
							});
						}
						resolve();
					})
					.catch((error) => {
						console.log(error);
						resolve();
					});
			});
		},
		updateBasketQuantity: (context, payload) => {
			return new Promise((resolve) => {
				axios
					.post("/umbraco/Checkout/Basket/Update", payload)
					.then((response) => {
						if (response.data.Success) {
							context.commit("updateBasket", { basket: response.data.Data });
							context.dispatch("getExpiredReservations");
							context.dispatch("toggleIfEmpty");
							context.commit("showBasketMessage");
						} else {
							alert(response.data.Message);
						}
						resolve();
					})
					.catch((error) => {
						console.log(error);
						resolve();
					});
			});
		},
		placeOrder: (context, payload) => {
			return new Promise((resolve, reject) => {
				context.dispatch("saveBasketNow", payload.basket).then((response) => {
					context.commit("isCheckingOut", true);
					axios
						.post("/umbraco/Checkout/Basket/PlaceOrder", {
							nodeId: payload.nodeId,
						})
						.then((response) => {
							console.log(response);
							if (response.data.Success) {
								console.log("success");
								console.log(response.data);
								sessionStorage.setItem("fcms_currentBasket", "{}");
								sessionStorage.removeItem("participantLookup");

								if (response.data.Message == "QuickPay") {
									window.location.href = response.data.Data;
									context.commit("isCheckingOut", false);
									context.commit("toggleConditionsAccepted");
									resolve();
								} else {
									axios.get(response.data.Data).then((receipt) => {
										let tempNode = document.createElement("div");
										tempNode.innerHTML = receipt.data;

										var receiptContent = tempNode.querySelector(
											".sitenav+.container"
										);
										if (receiptContent) {
											history.replaceState(
												{ shouldReload: true },
												null,
												response.data.Data
											);
											context.commit(
												"setReceiptData",
												receiptContent.innerHTML
											);
											context.commit("isCheckingOut", false);
											context.commit("toggleConditionsAccepted");
											resolve();
										} else {
											window.location.href = response.data.Data;
											context.commit("isCheckingOut", false);
											context.commit("toggleConditionsAccepted");
											reject();
										}
									});
								}
							} else {
								alert(response.data.Message);
								context.commit("isCheckingOut", false);
								reject();
							}
						})
						.catch((response) => {
							console.log(response);
							alert("Kunne ikke fuldføre ordren");

							axios.post("/umbraco/api/Log/LogError", {
								exception: "Ordre ikke fuldført",
								data: { response },
								method: "JavascriptException",
							});
							context.commit("isCheckingOut", false);
							resolve();
						});
				});
			});
		},
		confirmMobilePayNumber: (context, payload) => {
			return new Promise((resolve) => {
				axios
					.post("/umbraco/Checkout/Basket/ConfirmMobilePayNumber", payload)
					.then((response) => {
						context.commit("updateMobilePayData", response.data.Data);
						resolve();
					});
			});
		},
		confirmMember: (context, payload) => {
			return new Promise((resolve) => {
				axios
					.post("/umbraco/Checkout/Basket/ConfirmMember", payload)
					.then((response) => {
						context.commit("updateMemberData", response.data.Data);
						resolve();
					});
			});
		},
	},
});

export default store;
